/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { createPortal } from 'react-dom';
import { Button, ModalBody, ModalFooter, ModalHeader, TextInput } from '@carbon/react';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';

import { NoDevClusterSelectedNotification } from 'App/Pages/Diagram/features/deployment/modals/play-configuration-modal/no-dev-cluster-notification';
import { useDeploymentStates } from 'App/Pages/Diagram/features/deployment/modals/deploy-modal/hooks';
import {
  ClusterSelection,
  useSelectFirstClusterByDefault
} from 'App/Pages/Diagram/features/deployment/cluster-selection';
import {
  ClientCredentials,
  useClientCredentialsValidation
} from 'App/Pages/Diagram/features/deployment/client-credentials';
import { RememberCredentials } from 'App/Pages/Diagram/features/deployment/remember-credentials';
import { Continue } from 'App/Pages/Diagram/features/deployment/actions';
import { Modal, ModalContent } from 'App/Pages/Diagram/features/deployment/modals/modal-base';
import { configuredClustersStore, processApplicationStore } from 'stores';

import { DevelopmentClusterSelection } from './development-cluster-selection';

function PlayDeployEndpointConfigModal({ onClose = () => {}, isOpen }) {
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [tenantId, setTenantId] = useState('');

  const deploymentStates = useDeploymentStates();

  const selectFirstClusterByDefaultCondition = !processApplicationStore.fromAProcessApplication;
  useSelectFirstClusterByDefault(
    configuredClustersStore.clusters,
    setSelectedCluster,
    selectFirstClusterByDefaultCondition
  );

  usePersistPlayClusterIdInLS(selectedCluster);

  const developmentClusterId = processApplicationStore.processApplication.defaultDevClusterId;
  const developmentCluster = configuredClustersStore.clusters.find((cluster) => cluster.id === developmentClusterId);
  const isNoDevelopmentClusterAssigned = !developmentClusterId;

  const { isValidationEnabled, enableValidation, isInputValidationSuccessful } = useClientCredentialsValidation({
    isOpen,
    clientId,
    clientSecret
  });

  let modalContent;
  if (processApplicationStore.fromAProcessApplication && isNoDevelopmentClusterAssigned) {
    modalContent = <NoDevClusterSelectedNotification isOpen={isOpen} onClose={onClose} />;
  } else {
    modalContent = (
      <Modal open={isOpen} preventCloseOnClickOutside size="lg" onClose={onClose}>
        <ModalHeader title={'Open Play'} />
        <ModalBody>
          <ModalContent>
            {processApplicationStore.fromAProcessApplication ? (
              <DevelopmentClusterSelection
                clusters={configuredClustersStore.clusters}
                developmentClusterId={developmentClusterId}
                closeModal={onClose}
              />
            ) : (
              <ClusterSelection
                clusters={configuredClustersStore.clusters}
                selectedCluster={selectedCluster}
                setSelectedCluster={setSelectedCluster}
                resetDeploymentState={deploymentStates.reset}
              />
            )}

            <ClientCredentials
              selectedCluster={processApplicationStore.fromAProcessApplication ? developmentCluster : selectedCluster}
              clientId={clientId}
              setClientId={setClientId}
              clientSecret={clientSecret}
              setClientSecret={setClientSecret}
              isValidationEnabled={isValidationEnabled}
            />
            <TextInput
              id="tenant-id-input"
              type="text"
              labelText="Tenant ID (Optional)"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
            />
            <RememberCredentials
              selectedCluster={processApplicationStore.fromAProcessApplication ? developmentCluster : selectedCluster}
              clientId={clientId}
              clientSecret={clientSecret}
              setClientId={setClientId}
              setClientSecret={setClientSecret}
            />
          </ModalContent>
        </ModalBody>
        <ModalFooter>
          <Button kind="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Continue
            selectedCluster={selectedCluster}
            isInputValidationSuccessful={isInputValidationSuccessful}
            enableValidation={enableValidation}
            closeModal={() => {
              persistInLS(tenantId, clientId, clientSecret);
              onClose();
            }}
          />
        </ModalFooter>
      </Modal>
    );
  }

  return isOpen ? createPortal(modalContent, document.body) : null;
}

export default observer(PlayDeployEndpointConfigModal);

const usePersistPlayClusterIdInLS = (selectedCluster) => {
  useEffect(() => {
    localStorage.setItem('playClusterId', selectedCluster?.id);
  }, [selectedCluster]);
};

const persistInLS = (tenantId, clientId, clientSecret) => {
  localStorage.setItem('playTenantId', tenantId);
  localStorage.setItem('playClientId', clientId);
  localStorage.setItem('playClientSecret', clientSecret);
};
